import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

/**
 * Theme architecture for the admin panel.
 * Sets <html data-theme="light|dark"> so the design-system CSS variables
 * switch automatically. Defaults to light; persists the choice.
 *
 * This is additive — it does not alter any existing rendering. Components
 * read CSS variables, so wrapping the app enables future dark mode with
 * zero per-component changes.
 */
const STORAGE_KEY = "admin-theme";
const ThemeContext = createContext({ theme: "light", setTheme: () => {}, toggleTheme: () => {} });

export const ThemeProvider = ({ children, defaultTheme = "light" }) => {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return defaultTheme;
    try {
      return localStorage.getItem(STORAGE_KEY) || defaultTheme;
    } catch {
      return defaultTheme;
    }
  });

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-theme", theme);
    }
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore storage errors */
    }
  }, [theme]);

  const setTheme = useCallback((t) => setThemeState(t === "dark" ? "dark" : "light"), []);
  const toggleTheme = useCallback(
    () => setThemeState((t) => (t === "dark" ? "light" : "dark")),
    []
  );

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeProvider;
